import { getRondoUserStats, type RondoUserStats } from '../api/rondo';
import { getMarketingDashboard, type MarketingDashboard } from '../api/marketing';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { useRondoPageData } from './useRondoPageData';
import './rondo-dashboard.css';

const count = (value: number) => Number(value || 0).toLocaleString('cs-CZ');
const countryName = (country: string) => ({ CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko' })[country] ?? country;

export function RondoDashboardPage() {
  const { data: users, loading: usersLoading } = useRondoPageData<RondoUserStats>('users-stats', getRondoUserStats);
  const { data: marketing, loading: marketingLoading } = useRondoPageData<MarketingDashboard>('marketing-dashboard', getMarketingDashboard);

  return (
    <section className="rondo-arc-home p1-arc-scope">
      <header className="prototype-page-head">
        <div>
          <div className="prototype-eyebrow rondo">RONDO</div>
          <h1>Dashboard</h1>
          <p>Aktuální přehled uživatelů, provozu a komunikace</p>
        </div>
      </header>

      <div className="rondo-arc-metrics">
        {users ? (
          <>
            <MetricCard label="Uživatelů celkem" value={users.total} context={`${count(users.active.d90)} aktivních za 90 dní`} />
            <MetricCard label="Aktivní za 30 dní" value={users.active.d30} context={`${count(users.active.d1)} dnes`} />
            <MetricCard label="Aktivní za 90 dní" value={users.active.d90} context={`${count(Math.max(0, users.total - users.active.d90))} mimo 90 dní`} />
          </>
        ) : (
          <>
            <DashboardMetricUnavailable label="Uživatelů celkem" message={usersLoading ? 'Data se načítají' : 'Data nejsou dostupná'} />
            <DashboardMetricUnavailable label="Aktivní za 30 dní" message={usersLoading ? 'Data se načítají' : 'Data nejsou dostupná'} />
            <DashboardMetricUnavailable label="Aktivní za 90 dní" message={usersLoading ? 'Data se načítají' : 'Data nejsou dostupná'} />
          </>
        )}
        {marketing ? (
          <MetricCard label="Odesláno za 30 dní" value={marketing.stats.sent30d} context={`${count(marketing.activeCampaigns)} aktivních kampaní`} />
        ) : (
          <DashboardMetricUnavailable label="Odesláno za 30 dní" message={marketingLoading ? 'Data se načítají' : 'Data nejsou dostupná'} />
        )}
      </div>

      <div className="rondo-arc-panels">
        <Card title="Uživatelé podle trhu" description="Aktivita a registrace za 30 dní" className="rondo-arc-panel rondo-arc-markets">
          {users ? (
            <div className="rondo-arc-market-list">
              {users.byCountry.map((country) => (
                <div className="rondo-arc-market" key={country.country}>
                  <div className="rondo-arc-market-name">
                    <span className="rondo-arc-country-mark">{country.country}</span>
                    <span><strong>{countryName(country.country)}</strong><small>{country.currency}</small></span>
                  </div>
                  <dl>
                    <div><dt>Aktivní</dt><dd>{count(country.active30d)}</dd></div>
                    <div><dt>Registrace</dt><dd>{count(country.registrace30d)}</dd></div>
                    <div><dt>Spin hráči</dt><dd>{count(country.spinUsers)}</dd></div>
                  </dl>
                </div>
              ))}
            </div>
          ) : <p className="rondo-arc-unavailable">{usersLoading ? 'Data se načítají.' : 'Data uživatelů se nepodařilo načíst.'}</p>}
        </Card>

        <Card
          title="Marketingový provoz"
          description="Fronta a stav služeb"
          className="rondo-arc-panel rondo-arc-marketing"
          action={marketing && <span className={`rondo-arc-health${marketing.services.every((service) => service.status === 'ok') ? ' is-ok' : ''}`}>{marketing.services.filter((service) => service.status === 'ok').length} / {marketing.services.length} v pořádku</span>}
        >
          {marketing ? (
            <>
              <div className="rondo-arc-queue">
                <div><span>Čeká</span><strong>{count(marketing.queue.pending)}</strong></div>
                <div><span>Zpracovává se</span><strong>{count(marketing.queue.processing)}</strong></div>
                <div><span>Odesláno</span><strong>{count(marketing.queue.sent)}</strong></div>
                <div><span>Chyby</span><strong>{count(marketing.queue.failed)}</strong></div>
              </div>
              <div className="rondo-arc-services">
                {marketing.services.map((service) => (
                  <div key={service.key}>
                    <span className={`rondo-arc-service-dot ${service.status}`} aria-hidden="true" />
                    <strong>{service.key}</strong>
                    <small>{service.status === 'ok' ? 'V pořádku' : service.status === 'running' ? 'Právě běží' : service.status === 'never' ? 'Dosud neběželo' : 'Vyžaduje kontrolu'}</small>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="rondo-arc-unavailable">{marketingLoading ? 'Data se načítají.' : 'Marketingová data se nepodařilo načíst.'}</p>}
        </Card>
      </div>
    </section>
  );
}

function DashboardMetricUnavailable({ label, message }: { label: string; message: string }) {
  return <Card title={label} description={message} className="rondo-arc-metric-unavailable"><span>—</span></Card>;
}
